import React, { useState } from 'react';
import { X, ArrowDownCircle, Check } from 'lucide-react';
import { safeFetch } from '../../../lib/api';
import { notifyToast } from '../../../lib/toastStore';
import { STAGE_ORDER, STAGE_LABELS, CanonicalStage } from '../../../lib/workflow/transitions';

interface DemoteModalProps {
  uid: string;
  applicantName?: string;
  currentStage: string;
  initialTargetStage?: string;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * Optimistic display hooks (drawer responsiveness fix): fired the
   * moment the admin confirms, before the request resolves, so the
   * drawer shows the target stage immediately; `onMoveFailed` rolls
   * that display back when the server refuses.
   */
  onOptimisticMove?: (stage: string) => void;
  onMoveFailed?: () => void;
}

/**
 * Admin stage demotion dialog.
 *
 * - Target stage is selectable but restricted to stages EARLIER than the
 *   current stage in STAGE_ORDER — never forward, never the current stage,
 *   never Closure.
 * - Demotion moves only the stage pointer; no data is deleted. The new
 *   stage's prerequisites recompute from existing data.
 * - The applicant carries an amber/orange "Demoted" badge until cleared.
 */
export const DemoteModal: React.FC<DemoteModalProps> = ({
  uid,
  applicantName,
  currentStage,
  initialTargetStage,
  onClose,
  onSuccess,
  onOptimisticMove,
  onMoveFailed,
}) => {
  const currentIdx = STAGE_ORDER.indexOf(currentStage as CanonicalStage);
  // Earlier stages only; 'closed' can never be a demotion target.
  const targetOptions = STAGE_ORDER.filter(
    (_, i) => i < currentIdx && STAGE_ORDER[i] !== 'closed'
  );

  const defaultTarget = initialTargetStage && targetOptions.includes(initialTargetStage as CanonicalStage)
    ? initialTargetStage
    : targetOptions[targetOptions.length - 1] || '';

  const [targetStage, setTargetStage] = useState<string>(defaultTarget);
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const currentLabel = (STAGE_LABELS as any)[currentStage] || currentStage;
  const targetLabel = (STAGE_LABELS as any)[targetStage] || targetStage;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetStage) {
      notifyToast('error', 'Select a target stage to demote to.');
      return;
    }
    if (!reason || reason.trim().length < 20) {
      notifyToast('error', 'A detailed reason of at least 20 characters is required for a demotion.');
      return;
    }

    setSubmitting(true);
    // Confirmed: the drawer shows the target stage from this moment;
    // a refusal below rolls that display back.
    onOptimisticMove?.(targetStage);
    try {
      const res = await safeFetch(`/api/admin/workflow/applicants/${uid}/demote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetStage,
          reason: reason.trim(),
        }),
      });

      if (res?.success) {
        notifyToast('success', `Demoted to ${targetLabel}`);
        onSuccess();
        onClose();
      } else {
        onMoveFailed?.();
      }
    } catch (err: any) {
      onMoveFailed?.();
      notifyToast('error', err.message || 'Demotion failed');
    } finally {
      setSubmitting(false);
    }
  };

  const isReasonValid = reason.trim().length >= 20;

  return (
    <div className="fixed inset-0 z-[9000] bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 animate-fade-in font-sans">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 space-y-4 overflow-hidden border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between pb-3 border-b border-slate-200">
          <div className="flex items-center gap-2 text-amber-900 font-extrabold text-sm">
            <ArrowDownCircle className="h-5 w-5 text-amber-600" />
            <span>Demote Applicant — {currentLabel}</span>
          </div>
          <button aria-label="Close" title="Close"
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 text-xs text-amber-950 space-y-1.5">
          <p className="font-extrabold">
            Moving {applicantName ? <span className="underline">{applicantName}</span> : 'this applicant'} from{' '}
            <span className="font-black">{currentLabel}</span> back to{' '}
            <span className="font-black">{targetLabel || '—'}</span>.
          </p>
          <p className="text-[11px] font-medium text-amber-800">
            Demotion moves only the stage pointer — no placements, screenings, invoices, or other
            data are deleted. The new stage's prerequisites recompute from the existing data. The
            application carries an amber <span className="font-bold">Demoted</span> badge until it is
            cleared or the applicant advances again. This action is permanently logged.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Target stage * <span className="font-medium text-slate-400">(earlier stages only)</span>
            </label>
            {targetOptions.length === 0 ? (
              <p className="text-xs text-slate-500 italic">No earlier stage available.</p>
            ) : (
              <select
                value={targetStage}
                onChange={(e) => setTargetStage(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-bold text-slate-800 outline-none focus:bg-white focus:border-amber-500 cursor-pointer"
              >
                {targetOptions.map((s) => (
                  <option key={s} value={s}>
                    {(STAGE_LABELS as any)[s] || s}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-bold text-slate-700">
                Mandatory Demotion Reason * (min 20 chars)
              </label>
              <span className={`text-[10px] font-mono font-bold ${isReasonValid ? 'text-emerald-600' : 'text-slate-400'}`}>
                {reason.trim().length}/20
              </span>
            </div>
            <textarea
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain exactly why this applicant is being moved back (e.g. Placement fell through — employer withdrew the offer, re-entering processing to find a new match)..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-medium text-slate-800 outline-none focus:bg-white focus:border-amber-500"
            />
          </div>

          <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !isReasonValid || !targetStage}
              className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white font-extrabold rounded-xl shadow-xs cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
              <span>{submitting ? 'Demoting...' : `Confirm Demote → ${targetLabel}`}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
